/**
 * Hand types (cheirognomy)
 *
 * the Sāmudrika tradition's seven types — Elementary, Square, Spatulate, Philosophic, Conic,
 * Psychic, Mixed — chosen over the four-element scheme because his is finer and
 * it is what the rule corpus is written against (Sāmudrika / Hasta Rekhā tradition,
 * Part I). The thumb's set, and the finger-tip shapes, are read here too.
 *
 * ⚠ **The numeric prototypes below are engineering priors, unvalidated.** the Sāmudrika tradition
 * describes each type in words and drawings, not in measurements; turning that
 * into a nearest-prototype classifier on three ratios is a translation, and it
 * is flagged `calibration: 'unvalidated'` in every result. Where the fit is not
 * clear the answer is **Mixed** — which is also what the tradition says most
 * hands are — with a low confidence.
 *
 * the Sāmudrika tradition insists (and the site repeats it) that the hand type must be
 * established *first*, because it changes how every line is read.
 *
 * © Manish Jagdish Thatte
 */

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

/** the Sāmudrika tradition's types and the ratios that stand in for his descriptions. */
export const HAND_TYPES = Object.freeze({
  elementary: { aspect: 0.95, finger: 0.62, tip: 0.95 },
  square: { aspect: 1.0, finger: 0.78, tip: 1.0 },
  spatulate: { aspect: 1.05, finger: 0.85, tip: 1.2 },
  philosophic: { aspect: 1.2, finger: 0.95, tip: 0.9 },
  conic: { aspect: 1.1, finger: 0.85, tip: 0.7 },
  psychic: { aspect: 1.3, finger: 1.0, tip: 0.45 }
});
const SPREAD = { aspect: 0.12, finger: 0.12, tip: 0.2 };

/**
 * Finger lengths and the ratios the Sāmudrika tradition uses.
 * Fingers are `[index, middle, ring, little]`; each length runs from the base of the
 * finger (midpoint of the two webs beside it) to its tip.
 */
export function fingerRatios(lm) {
  const [thumbTip, ...tips] = lm.tips; void thumbTip;
  const w = lm.webs;
  // Only the three inner webs are used: the thumb–index web sits far down the
  // hand and would make the index finger look longer than it is.
  const beyond = (a, b) => [a[0] + (a[0] - b[0]) * 0.5, a[1] + (a[1] - b[1]) * 0.5];
  const bases = [beyond(w[1], w[2]), mid(w[1], w[2]), mid(w[2], w[3]), beyond(w[3], w[2])];
  const lengths = tips.map((t, i) => dist(bases[i], t));
  const palmLength = dist(lm.wrist, lm.webMid);
  const palmWidth = lm.palmWidth;
  const [index, middle, ring, little] = lengths;
  return {
    lengths, bases, palmLength, palmWidth,
    aspect: palmLength / palmWidth,
    middleToPalm: middle / palmLength,
    indexToRing: index / ring,
    littleToRing: little / ring,
    // the Sāmudrika tradition's Jupiter / Saturn / Apollo / Mercury finger comparisons
    indexVsRing: index > ring * 1.03 ? 'longer' : index < ring * 0.97 ? 'shorter' : 'equal'
  };
}

/** Width of the mask across the axis at fraction `t` from the tip toward the base. */
function widthAt(mask, base, tip, t) {
  const px = tip[0] + (base[0] - tip[0]) * t; const py = tip[1] + (base[1] - tip[1]) * t;
  const ax = tip[0] - base[0]; const ay = tip[1] - base[1]; const al = Math.hypot(ax, ay) || 1;
  const nx = -ay / al; const ny = ax / al;
  const run = (s) => { let r = 0; for (; r < 200; r++) { const x = Math.round(px + nx * s * r); const y = Math.round(py + ny * s * r); if (x < 0 || y < 0 || x >= mask.width || y >= mask.height || !mask.data[y * mask.width + x]) break; } return r; };
  return run(1) + run(-1);
}

/**
 * Tip shape of one finger from the mask: the ratio of the width near the tip
 * (15 % from it) to the width lower down (55 %).
 * ≈0.45 pointed · ≈0.7 conic · ≈1.0 square · >1.1 spatulate (widening).
 */
export function fingerShape(mask, base, tip) {
  const near = widthAt(mask, base, tip, 0.15); const low = widthAt(mask, base, tip, 0.55);
  const ratio = low ? near / low : NaN;
  const label = !Number.isFinite(ratio) ? 'indeterminate' : ratio < 0.55 ? 'pointed' : ratio < 0.85 ? 'conic' : ratio < 1.1 ? 'square' : 'spatulate';
  return { ratio, label };
}

/** The thumb's angle to the index finger, in degrees, and the Sāmudrika tradition's "set". */
export function thumbAngle(lm) {
  const w = lm.webs[0];
  const a = [lm.tips[0][0] - w[0], lm.tips[0][1] - w[1]]; const b = [lm.tips[1][0] - w[0], lm.tips[1][1] - w[1]];
  const cos = (a[0] * b[0] + a[1] * b[1]) / ((Math.hypot(...a) * Math.hypot(...b)) || 1);
  const degrees = Math.acos(Math.max(-1, Math.min(1, cos))) * 180 / Math.PI;
  // ⚠ the angle depends on how the hand was held in this one photograph — a posture, not a trait, hence the low weight downstream.
  return { degrees, set: degrees < 35 ? 'close' : degrees < 65 ? 'medium' : 'wide', posture: true };
}

/**
 * Classify the hand into the Sāmudrika tradition's types.
 * Returns `{ type, confidence, scores, ratios, tipRatio, calibration, note }`.
 */
export function handType(lm, mask = null) {
  const ratios = fingerRatios(lm);
  let tipRatio = null; const shapes = [];
  if (mask) {
    for (let i = 0; i < 4; i++) shapes.push(fingerShape(mask, ratios.bases[i], lm.tips[i + 1]));
    const ok = shapes.map((s) => s.ratio).filter(Number.isFinite);
    tipRatio = ok.length ? ok.reduce((a, b) => a + b, 0) / ok.length : null;
  }
  const scores = {};
  for (const [k, p] of Object.entries(HAND_TYPES)) {
    let e = ((ratios.aspect - p.aspect) / SPREAD.aspect) ** 2 + ((ratios.middleToPalm - p.finger) / SPREAD.finger) ** 2;
    let n = 2;
    if (tipRatio != null) { e += ((tipRatio - p.tip) / SPREAD.tip) ** 2; n++; }
    scores[k] = Math.exp(-0.5 * e / n * 1.5);
  }
  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [best, second] = ranked;
  const clear = best[1] >= 0.4 && best[1] - second[1] >= 0.1;
  const type = clear ? best[0] : 'mixed';
  const confidence = clear ? Math.min(0.8, best[1] * (0.5 + 0.5 * (best[1] - second[1]) / best[1])) : Math.min(0.4, best[1]);
  return {
    type, confidence, scores, ratios, tipRatio, shapes,
    calibration: 'unvalidated',
    note: tipRatio == null ? 'Finger-tip shape was not measured; the type rests on proportions alone.' : null
  };
}
